import React from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { listPublicBanners } from '@/api/contentService';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { createPageUrl } from "@/utils";
import { 
  Heart, 
  HeartHandshake,
  Radio, 
  Church, 
  MapPin, 
  Image, 
  Play,
  Bell,
  ChevronRight,
  X,
  Check,
  Star,
  Phone,
  Mail,
} from 'lucide-react';
import { motion } from 'framer-motion';
import BannerCarousel from '@/components/ui/BannerCarousel';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Button } from "@/components/ui/button";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from "sonner";
import { clearAppIconBadge } from '@/lib/appIconBadge';
import { canViewEventByAudience, getMemberGroupIds } from '@/lib/eventAudienceVisibility';
import { navigateFromNotification } from '@/lib/notificationRouting';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';

export default function Home() {
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [showFullDonationText, setShowFullDonationText] = React.useState(false);
  const parseEventDate = (dateStr) => {
    if (!dateStr) return null;
    const normalized = String(dateStr).slice(0, 10);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return new Date(dateStr);
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    const openNotifications = () => setShowNotifications(true);
    window.addEventListener('mbv:open-notifications', openNotifications);
    return () => window.removeEventListener('mbv:open-notifications', openNotifications);
  }, []);

  React.useEffect(() => {
    if (searchParams.get('notifications') !== '1') return;
    setShowNotifications(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('notifications');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);
  const { data: banners = [], isLoading: loadingBanners } = useQuery({
    queryKey: ['banners'],
    queryFn: () => listPublicBanners(20)
  });

  const { data: churchInfo } = useQuery({
    queryKey: ['churchInfo'],
    queryFn: async () => {
      const info = await base44.entities.ChurchInfo.list();
      return info[0] || null;
    }
  });

  const { data: rawUpcomingEvents = [] } = useQuery({
    queryKey: ['upcomingEventsRaw'],
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const events = await base44.entities.Event.filter(
        { status: 'Ativo', date: { $gte: today } },
        'date',
        50
      );
      return events;
    }
  });

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
    retry: false
  });

  const { data: currentMember } = useQuery({
    queryKey: ['currentMember', currentUser?.email],
    queryFn: () => base44.entities.Member.filter({ email: currentUser.email }),
    enabled: !!currentUser?.email
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: () => base44.entities.Group.filter({ is_active: true })
  });

  const member = currentMember?.[0] || null;
  const memberGroupIds = getMemberGroupIds(member, groups);
  const upcomingEvents = rawUpcomingEvents
    .filter((event) => canViewEventByAudience(event, { member, memberGroupIds }))
    .slice(0, 3);

  const currentMemberId = member?.id || null;

  const { data: unreadNotifications = [] } = useUnreadNotifications(currentUser, currentMemberId);

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId) => {
      return base44.entities.Notification.update(notificationId, { is_read: true });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] });
      clearAppIconBadge().catch(() => {});
    }
  });

  const handleNotificationClick = async (notification) => {
    await base44.entities.Notification.update(notification.id, { is_read: true });
    queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] });
    clearAppIconBadge().catch(() => {});
    setShowNotifications(false);
    navigateFromNotification(navigate, notification);
  };

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const updates = unreadNotifications.map(n => 
        base44.entities.Notification.update(n.id, { is_read: true })
      );
      return Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unreadNotifications'] });
      clearAppIconBadge().catch(() => {});
      toast.success('Todas as notificações foram marcadas como lidas');
      setShowNotifications(false);
    }
  });

  const churchLogo = churchInfo?.logo_url || '';
  const footerLogo = churchInfo?.footer_logo_url || churchLogo;
  const donationText =
    churchInfo?.donation_text ||
    'A doação é uma forma de contribuir com os projetos e com a obra de Deus.';
  const mobileDonationMaxChars = 140;
  const shouldCollapseDonationText = donationText.length > mobileDonationMaxChars;

  return (
    <div className="mbv-home">
      <section className="mbv-hero">
        <div className="mbv-hero-copy">
          <span className="mbv-eyebrow">{churchInfo?.name || 'MBV Igreja'}</span>
          <h1>Um lugar para viver a fé. <em>juntos.</em></h1>
          <p>{churchInfo?.mission || 'Conexões reais, encontros que transformam e espaço para você.'}</p>
          <div className="mbv-hero-links">
            <Link className="mbv-primary-button" to={createPageUrl('QuemSomos')}>Conheça a MBV <ChevronRight size={20} /></Link>
            <Link className="mbv-text-link" to={createPageUrl('NossosCultos')}>Nossos cultos <ChevronRight size={18} /></Link>
          </div>
        </div>
        <div className="mbv-hero-media">
          {loadingBanners ? <div className="mbv-hero-loading"><LoadingSpinner /></div> : banners.length > 0 ? (
            <BannerCarousel banners={banners} />
          ) : (
            <div className="mbv-hero-placeholder"><img src={churchLogo || '/mbv-logo-512.png'} alt={churchInfo?.name || 'MBV Igreja'} /></div>
          )}
          <div className="mbv-hero-circle" aria-hidden="true" />
        </div>
      </section>

      <div className="mbv-action-strip" aria-label="Acessos principais">
        <Link to={createPageUrl('NossosCultos')}><Church size={24} /><span>Nossos cultos</span><ChevronRight size={20} /></Link>
        <Link to={createPageUrl('Transmissao')}><Radio size={24} /><span>Assistir ao vivo</span><ChevronRight size={20} /></Link>
        <Link to={createPageUrl('PedidoOracao')}><Heart size={24} /><span>Pedir oração</span><ChevronRight size={20} /></Link>
      </div>

      {/* O conteúdo, as imagens e os links abaixo continuam vindo das fontes atuais. */}
      <div className="mbv-home-content">

        {/* Ver todos avisos */}
        {banners.length > 0 && (
          <Link to={createPageUrl('Avisos')}>
            <div className="mbv-inline-link flex items-center justify-end gap-1 mt-3 text-sm font-medium">
              Ver todos os avisos <ChevronRight className="w-4 h-4" />
            </div>
          </Link>
        )}

        {/* Doações */}
        <div className="mbv-donation-section mt-8">
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
            <div className="grid md:grid-cols-2">
              <div className="h-56 md:h-full bg-gray-100">
                {churchInfo?.donation_image_url ? (
                  <img
                    src={churchInfo.donation_image_url}
                    alt="Dízimos e Ofertas"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-200 to-zinc-300">
                    <HeartHandshake className="w-14 h-14 text-red-600" />
                  </div>
                )}
              </div>
              <div className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <HeartHandshake className="w-8 h-8 text-red-600" />
                  <h3 className="text-3xl font-bold text-red-600">
                    {churchInfo?.donation_title || 'Dízimos e Ofertas'}
                  </h3>
                </div>
                <p
                  className={`text-zinc-700 leading-relaxed mb-2 md:mb-6 transition-all ${
                    showFullDonationText ? 'max-h-none' : 'max-h-20 overflow-hidden md:max-h-none'
                  }`}
                >
                  {churchInfo?.donation_text ||
                    'A doação é uma forma de contribuir com os projetos e com a obra de Deus.'}
                </p>
                {shouldCollapseDonationText && (
                  <button
                    type="button"
                    onClick={() => setShowFullDonationText((prev) => !prev)}
                    className="md:hidden mb-6 text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    {showFullDonationText ? 'Ler menos' : 'Ler mais'}
                  </button>
                )}
                <Link to={createPageUrl('Doacoes')}>
                  <Button className="w-full h-12 bg-red-600 hover:bg-red-700 text-white text-base font-semibold">
                    Contribua
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <div id="proximos-eventos" className="mbv-events-section mt-8 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Próximos Eventos</h2>
              <Link to={createPageUrl('Agenda')} className="text-indigo-600 text-sm font-medium">
                Ver todos
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {upcomingEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() =>
                    navigate(
                      `${createPageUrl('Agenda')}?eventId=${event.id}&returnTo=home-upcoming`
                    )
                  }
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-xl flex flex-col items-center justify-center">
                      <span className="text-xs text-indigo-600 font-medium">
                        {parseEventDate(event.date).toLocaleDateString('pt-BR', { month: 'short' }).toUpperCase()}
                      </span>
                      <span className="text-lg font-bold text-indigo-700">
                        {parseEventDate(event.date).getDate()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{event.title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {event.start_time && `${event.start_time}`}
                        {event.location && ` • ${event.location}`}
                      </p>
                    </div>
                    <ChevronRight className="mbv-event-arrow" size={22} aria-hidden="true" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* More Options */}
        <div className="mbv-more-section mt-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Mais opções</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Link to={createPageUrl('QuemSomos')}>
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow h-full">
                <Church className="w-6 h-6 md:w-8 md:h-8 text-indigo-600 mb-2 md:mb-3" />
                <h3 className="font-medium text-gray-900 text-sm md:text-base">Quem Somos</h3>
                <p className="text-xs md:text-sm text-gray-500 mt-1">Conheça nossa história</p>
              </div>
            </Link>
            <Link to={createPageUrl('Galeria')}>
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow h-full">
                <Image className="w-6 h-6 md:w-8 md:h-8 text-indigo-600 mb-2 md:mb-3" />
                <h3 className="font-medium text-gray-900 text-sm md:text-base">Galeria</h3>
                <p className="text-xs md:text-sm text-gray-500 mt-1">Fotos e momentos</p>
              </div>
            </Link>
            <Link to={createPageUrl('Videos')}>
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow h-full">
                <Play className="w-6 h-6 md:w-8 md:h-8 text-indigo-600 mb-2 md:mb-3" />
                <h3 className="font-medium text-gray-900 text-sm md:text-base">Vídeos</h3>
                <p className="text-xs md:text-sm text-gray-500 mt-1">Mensagens e cultos</p>
              </div>
            </Link>
            <Link to={createPageUrl('Avaliacoes')}>
              <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow h-full">
                <Star className="w-6 h-6 md:w-8 md:h-8 text-yellow-500 mb-2 md:mb-3" />
                <h3 className="font-medium text-gray-900 text-sm md:text-base">Avaliações</h3>
                <p className="text-xs md:text-sm text-gray-500 mt-1">Deixe sua opinião</p>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer Home */}
      <footer className="mbv-home-footer text-white">
        <div className="mbv-footer-inner">
          <div className="mbv-footer-main">
            <div className="mbv-footer-brand">
              <img src={footerLogo || '/mbv-logo-512.png'} alt={churchInfo?.name || 'MBV Igreja'} />
              <h3>{churchInfo?.name || 'MBV Igreja'}</h3>
            </div>

            <div className="mbv-footer-column">
              <h4>Contato</h4>
              {(churchInfo?.footer_contact_phone || churchInfo?.phone) && <p><Phone size={16} />{churchInfo?.footer_contact_phone || churchInfo?.phone}</p>}
              {(churchInfo?.footer_contact_email || churchInfo?.email) && <p><Mail size={16} />{churchInfo?.footer_contact_email || churchInfo?.email}</p>}
            </div>

            <div className="mbv-footer-column">
              <h4>Endereço</h4>
              {(churchInfo?.footer_address || churchInfo?.address) && <p><MapPin size={16} />{churchInfo?.footer_address || churchInfo?.address}</p>}
            </div>

            <div className="mbv-footer-column">
              <h4>Suporte</h4>
              {churchInfo?.support_phone && <p><Phone size={16} />{churchInfo.support_phone}</p>}
              {churchInfo?.support_email && <p><Mail size={16} />{churchInfo.support_email}</p>}
            </div>
          </div>
          <div className="mbv-footer-bottom"><span>© {new Date().getFullYear()} {churchInfo?.name || 'MBV Igreja'}.</span></div>
        </div>
      </footer>

      {/* Notifications Modal */}
      {showNotifications && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[80vh] overflow-hidden"
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                Notificações ({unreadNotifications.length})
              </h3>
              <button onClick={() => setShowNotifications(false)}>
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[calc(80vh-120px)] px-6 py-4">
              {unreadNotifications.length === 0 ? (
                <div className="text-center py-12">
                  <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">Nenhuma notificação não lida</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {unreadNotifications.map((notification) => (
                    <div
                      key={notification.id}
                      className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 cursor-pointer hover:bg-indigo-100 transition-colors"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {notification.title}
                          </h4>
                          <p className="text-sm text-gray-600 mb-2">
                            {notification.message}
                          </p>
                          <span className="text-xs text-gray-500">
                            {new Date(notification.created_date).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsReadMutation.mutate(notification.id);
                          }}
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {unreadNotifications.length > 0 && (
              <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4">
                <Button
                  onClick={() => markAllAsReadMutation.mutate()}
                  disabled={markAllAsReadMutation.isPending}
                  className="w-full"
                >
                  Marcar todas como lidas
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}
